// @ts-strict-ignore
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { uploadVehiclePhoto } from '@fc-vehicles/store';
import { MatDialog } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, of } from 'rxjs';
import {
  NotificationService,
  Vehicle,
  VehicleApiService,
  VehicleType,
} from '@fc-core';

import * as fromVehicleActions from '../actions/vehicle.actions';
import * as fromVehicleSelectors from '../selectors/vehicle.selectors';
import { VehicleService } from '@fc-vehicles/service/vehicle.service';
import {
  initVehicleFilters,
  toggleVehicleInGroups,
  updateCreatedVehicleGroup,
} from '../actions';
import { getErrorMessage } from '@fc-core/services/notification.utils';

@Injectable()
export class VehicleEffects {
  private waitingForResponse: boolean;

  loadVehicles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromVehicleActions.loadVehicles),
      withLatestFrom(
        this.store.pipe(
          select(fromVehicleSelectors.getAllVehiclesWithCurrentOptions),
        ),
      ),
      switchMap(([type, vehicleData]) => {
        // if we try to load vehicles while we are waiting for response start load again
        if (this.waitingForResponse) {
          this.waitingForResponse = false;
          return [
            fromVehicleActions.reloadVehicles(),
            fromVehicleActions.loadVehicles({ options: type.options }),
          ];
        }

        const requestedVehicleOptions = type.options.filter(
          (option) => !vehicleData?.options?.includes(option),
        );

        if (requestedVehicleOptions.length === 0) {
          // if we had load all vehicles options just cancel load
          return [
            fromVehicleActions.cancelLoadVehicles(),
            fromVehicleActions.setLoaded(),
          ];
        }

        this.waitingForResponse = true;

        return this.vehicleApi
          .loadAllVehicleAsync<Vehicle>(requestedVehicleOptions)
          .pipe(
            switchMap(
              (vehiclePayload: {
                vehicleList: Vehicle[];
                count: number;
                totalLoaded: number;
                end: boolean;
              }) => {
                const actions = [];

                // update vehicles only when we load all of them
                if (
                  vehicleData.vehicles.length &&
                  vehicleData.vehicles.length === vehiclePayload.count
                ) {
                  actions.push(
                    fromVehicleActions.updateVehicles({
                      vehicles: vehiclePayload.vehicleList,
                      count: vehiclePayload.count,
                      fields: type.options,
                      totalLoaded: vehiclePayload.totalLoaded,
                    }),
                  );
                } else {
                  actions.push(
                    fromVehicleActions.loadVehiclesSuccess({
                      vehicles: vehiclePayload.vehicleList,
                      count: vehiclePayload.count,
                      fields: type.options,
                      totalLoaded: vehiclePayload.totalLoaded,
                    }),
                  );
                }

                if (
                  (vehiclePayload.end && vehiclePayload.count > 10) ||
                  vehiclePayload.count <= 10
                ) {
                  // reset waiting for response flag when it's last request
                  this.waitingForResponse = false;
                  actions.push(fromVehicleActions.setLoaded());
                }

                return actions;
              },
            ),
            catchError((error) =>
              of(fromVehicleActions.vehiclesError({ error })),
            ),
          );
      }),
    ),
  );

  loadVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromVehicleActions.loadVehicleData),
      switchMap(({ vehicleId, options }) => {
        return this.vehicleApi.loadVehicle(vehicleId, options).pipe(
          map((vehicle: Vehicle) =>
            fromVehicleActions.updateVehicleSuccess({
              vehicle: { id: vehicle.id, changes: vehicle },
            }),
          ),
          catchError((error) =>
            of(fromVehicleActions.vehiclesError({ error })),
          ),
        );
      }),
    ),
  );

  addVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromVehicleActions.addVehicle),
      switchMap(({ vehicle }) => {
        return this.vehicleApi.create<Vehicle>(vehicle).pipe(
          switchMap((vehicleItem: Vehicle) => {
            this.notifications.showSuccess('Vehicle successfully added');
            this.dialog.closeAll();
            this.router.navigate(['/vehicles/main/vehicles/', vehicleItem.id]);
            if (vehicle.photo) {
              return [
                fromVehicleActions.addVehicleSuccess({ vehicle: vehicleItem }),
                uploadVehiclePhoto({
                  vehicleId: vehicleItem.id,
                  sources: [
                    {
                      source: vehicle.photo as File,
                      isPrimary: true,
                      description: '',
                    },
                  ],
                }),
                updateCreatedVehicleGroup({ vehicle: vehicleItem }),
              ];
            }
            return [
              fromVehicleActions.addVehicleSuccess({ vehicle: vehicleItem }),
              updateCreatedVehicleGroup({ vehicle: vehicleItem }),
            ];
          }),
          catchError((error) =>
            of(fromVehicleActions.vehiclesError({ error })),
          ),
        );
      }),
    ),
  );

  updateVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromVehicleActions.updateVehicle),
      switchMap(({ vehicle, fields }) => {
        return this.vehicleApi.patchVehicle<Vehicle>(vehicle, fields).pipe(
          switchMap((vehiclePayload: Vehicle) => {
            this.notifications.showSuccess('Vehicle successfully updated');
            this.dialog.closeAll();
            return [
              fromVehicleActions.updateVehicleSuccess({
                vehicle: { id: vehiclePayload.id, changes: vehiclePayload },
              }),
              toggleVehicleInGroups({
                vehicle: vehicle.id,
                groupsIds: vehicle.groups,
              }),
            ];
          }),
          catchError((error) => {
            return of(fromVehicleActions.vehiclesError({ error }));
          }),
        );
      }),
    ),
  );

  deleteVehicle$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromVehicleActions.deleteVehicle),
      switchMap(({ id }) => {
        return this.vehicleApi.delete(id).pipe(
          map(() => {
            this.notifications.showSuccess('Vehicle successfully deleted');
            this.dialog.closeAll();
            this.router.navigate(['/vehicles/main/vehicles/']);
            return fromVehicleActions.deleteVehicleSuccess({ id });
          }),
          catchError((error) =>
            of(fromVehicleActions.vehiclesError({ error })),
          ),
        );
      }),
    );
  });

  loadVehicleTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromVehicleActions.loadVehicleTypes),
      switchMap(() => {
        return this.vehicleService.getVehicleTypes().pipe(
          switchMap((vehicleTypes: VehicleType[]) => [
            fromVehicleActions.loadVehicleTypesSuccess({ vehicleTypes }),
          ]),
          catchError((error) =>
            of(fromVehicleActions.vehiclesError({ error })),
          ),
        );
      }),
    ),
  );

  loaUsedVehicleTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromVehicleActions.loadUsedVehicleTypes),
      switchMap(() => {
        return this.vehicleService.getVehicleTypes(true).pipe(
          switchMap((vehicleTypes: VehicleType[]) => [
            fromVehicleActions.loadUsedVehicleTypesSuccess({ vehicleTypes }),
            initVehicleFilters({ vehicleTypes }),
          ]),
          catchError((error) =>
            of(fromVehicleActions.vehiclesError({ error })),
          ),
        );
      }),
    ),
  );

  setVehicleLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromVehicleActions.setVehicleLocation),
      switchMap(({ vehicleId, point, locationId }) => {
        return this.vehicleApi.setVehicleLocation(vehicleId, point).pipe(
          map((_) =>
            fromVehicleActions.setVehicleLocationSuccess({
              vehicleId,
              point,
              locationId,
            }),
          ),
          catchError((error) =>
            of(fromVehicleActions.vehiclesError({ error })),
          ),
        );
      }),
    );
  });

  vehiclesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromVehicleActions.vehiclesError),
        switchMap((action) => {
          if (action.error) {
            this.notifications.showError(getErrorMessage(action.error));
          }
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private vehicleApi: VehicleApiService,
    private vehicleService: VehicleService,
    private store: Store,
    private dialog: MatDialog,
    private router: Router,
    private notifications: NotificationService,
  ) {}
}
